import React, { useEffect, useState, useMemo } from "react";
import SubscriptionManager from "../../services/dashboard/SubscriptionManager";
import { useNavigate } from "react-router-dom";
import { UserRound } from "lucide-react";

const ExpiringSubscribers = () => {
  const [allSubscribedUsers, setAllSubscribedUsers] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  // 1) Fetch the full list once, on mount:
  useEffect(() => {
    const fetchSubscribedUsers = async () => {
      setLoading(true);
      try {
        const data = await SubscriptionManager.getAllSubscribedUsersWithDetails();
        setAllSubscribedUsers(data || []);
      } catch (error) {
        console.error("Failed to fetch subscribed users:", error);
        setAllSubscribedUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscribedUsers();
  }, []);

  // 2) Compute expiring users (remainingDays < 7) whenever allSubscribedUsers changes:
  const expiringUsers = useMemo(() => {
    return allSubscribedUsers.filter((u) => u.remainingDays < 7);
  }, [allSubscribedUsers]);

  // 3) Initialize displayedUsers to expiringUsers (or re‐initialize after data loads):
  useEffect(() => {
    setDisplayedUsers(expiringUsers);
  }, [expiringUsers]);

  // 4) When searchTerm changes, filter expiringUsers locally:
  useEffect(() => {
    if (searchTerm.trim() === "") {
      // If search box is empty, show all expiring users
      setDisplayedUsers(expiringUsers);
      return;
    }

    const lower = searchTerm.trim().toLowerCase();
    const filtered = expiringUsers.filter((u) => {
      const nameMatch = u.userName?.toLowerCase().includes(lower);
      const emailMatch = u.emailId?.toLowerCase().includes(lower);
      const planMatch = u.planName?.toLowerCase().includes(lower);
      return nameMatch || emailMatch || planMatch;
    });

    setDisplayedUsers(filtered);
  }, [searchTerm, expiringUsers]);

  const formatDateArray = (dateArray) => {
    if (
      !Array.isArray(dateArray) ||
      dateArray.length < 6 ||
      dateArray.some((n) => typeof n !== "number")
    ) {
      return "N/A";
    }
    const [year, month, day, hour, minute, second] = dateArray;
    const date = new Date(year, month - 1, day, hour, minute, second);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleViewUserDetails = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  return (
    <div className="min-h-screen bg-primary-shade1 p-6 font-nunito">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-text-highlight">
          Expiring Subscribers
        </h1>
        <input
          type="text"
          placeholder="Search by name, email, or plan..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-shade2"
        />
      </div>

      {loading ? (
        <div className="text-center text-lg text-text-muted">Loading...</div>
      ) : (
        <div className="overflow-x-auto bg-white shadow-xl rounded-2xl">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-primary text-white">
              <tr>
                <th className="px-6 py-3 text-left font-semibold">User ID</th>
                <th className="px-6 py-3 text-left font-semibold">Name</th>
                <th className="px-6 py-3 text-left font-semibold">Plan</th>
                <th className="px-6 py-3 text-left font-semibold">Package</th>
                <th className="px-6 py-3 text-left font-semibold">
                  Remaining Days
                </th>
                <th className="px-6 py-3 text-left font-semibold">End Date</th>
                <th className="px-6 py-3 text-left font-semibold">Status</th>
                <th className="px-6 py-3 text-center font-semibold">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayedUsers.map((user) => (
                <tr
                  key={user.userId}
                  className="hover:bg-primary-shade1 transition duration-150 ease-in-out"
                >
                  <td className="px-6 py-4">{user.userId}</td>
                  <td className="px-6 py-4 font-medium text-gray-800">
                    {user.userName}
                  </td>
                  <td className="px-6 py-4">
                    <span className="bg-primary-shade1 text-primary-shade5 px-2 py-1 rounded-full text-xs font-semibold">
                      {user.planName}
                    </span>
                  </td>
                  <td className="px-6 py-4">{user.packageType}</td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                      {user.remainingDays} days
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {formatDateArray(user.endDate)}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        user.status === "ACTIVE"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => handleViewUserDetails(user.userId)}
                      className="text-primary hover:text-primary-shade5 transition"
                      title="View User Details"
                    >
                      <UserRound className="inline-block text-lg" />
                    </button>
                  </td>
                </tr>
              ))}

              {displayedUsers.length === 0 && !loading && (
                <tr>
                  <td colSpan="8" className="text-center py-6 text-gray-500">
                    {searchTerm
                      ? `No expiring users match “${searchTerm}.”`
                      : "No expiring subscribers at the moment."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ExpiringSubscribers;
